import { Link } from '../catalyst/link'
import { filterOptions, PickerInput, type PickerOption } from './picker-input'
import type { Asset } from './types'

/**
 * A building or equipment name: suggests the inventory's assets first, then names already typed on
 * this farm's logs ("Used before"). Picking (or typing) an asset's name links the entry to it;
 * anything else is saved as typed. The line under the box says which, so a grower can tell a linked
 * entry apart. Wrap it in a Catalyst `Field` with a `Label`.
 */
export function AssetNameInput({
  assets,
  pastNames,
  linkedAssetId,
  inventoryHref,
  value,
  onChange,
  ...inputProps
}: {
  assets: Asset[]
  pastNames: string[]
  /** The asset the current name resolves to (see `assetIdFor`), or null. */
  linkedAssetId: string | null
  inventoryHref: string
  value: string
  /** The name as typed or picked; the caller resolves it to an asset with `assetIdFor`. */
  onChange: (name: string) => void
} & Pick<React.ComponentPropsWithoutRef<'input'>, 'placeholder' | 'required' | 'autoFocus'> & { invalid?: boolean }) {
  const known = new Set(assets.map((a) => a.name.toLowerCase()))
  const inventory: PickerOption[] = assets.map((a) => ({
    id: a.id,
    label: a.name,
    detail: [a.type, a.makeModel].filter(Boolean).join(' · '),
    badge: a.kind === 'MOVABLE' ? 'All farms' : undefined,
  }))
  const usedBefore: PickerOption[] = pastNames.filter((n) => !known.has(n.toLowerCase())).map((n) => ({ id: `name:${n}`, label: n }))
  const linked = assets.find((a) => a.id === linkedAssetId)
  const typed = value.trim()

  return (
    <PickerInput
      {...inputProps}
      value={value}
      onChange={onChange}
      onPick={(option) => onChange(option.label)}
      groups={[
        { label: 'Inventory', options: filterOptions(inventory, value) },
        { label: 'Used before', options: filterOptions(usedBefore, value) },
      ]}
      linked={linked ? ['In inventory', linked.type, linked.makeModel].filter(Boolean).join(' · ') : linkedAssetId ? 'In inventory' : null}
      hint={
        typed ? (
          <>
            Not in the <Link href={inventoryHref}>inventory</Link>: saved as typed.
          </>
        ) : undefined
      }
      listLabel="Inventory and names used before"
      emptyText={assets.length === 0 && pastNames.length === 0 ? 'Nothing in the inventory yet. Type the name.' : 'No match. Keep typing the name.'}
    />
  )
}
